/**
 * useRealtime — shared SSE hook.
 *
 * Opens an EventSource against `/api/realtime/stream`, listens for events
 * on the requested topics, and re-runs `onEvent` for every push. Handles
 * cleanup on unmount so React Strict Mode's double-mount doesn't leak
 * connections.
 *
 * Usage:
 *   useRealtime({ topics: ["cash-app"], onEvent: (e) => refresh() });
 */
import { useEffect, useRef } from "react";
import { getToken } from "@/lib/api";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

export function useRealtime({ topics = ["cash-app"], onEvent, adminToken }) {
  const token = getToken();
  const esRef = useRef(null);

  useEffect(() => {
    // Need either user auth OR admin token to open the channel.
    if (!token && !adminToken) return;
    const t = topics.join(",");
    const qs = new URLSearchParams({ topics: t });
    if (token) qs.set("token", token);
    if (adminToken) qs.set("admin_token", adminToken);
    const url = `${API}/api/realtime/stream?${qs.toString()}`;
    let es;
    try {
      es = new EventSource(url);
    } catch (e) {
      console.warn("useRealtime: EventSource unsupported", e);
      return;
    }
    esRef.current = es;

    const handler = (kind) => (msg) => {
      try {
        const data = JSON.parse(msg.data);
        onEvent?.({ topic: kind, ...data });
      } catch {
        /* ignore malformed */
      }
    };
    topics.forEach((kind) => es.addEventListener(kind, handler(kind)));
    es.addEventListener("ready", handler("ready"));

    es.onerror = () => {
      // Browsers auto-reconnect — but if the connection won't open at all
      // (bad token, 401) we close it explicitly so we don't reconnect-loop.
      if (es.readyState === EventSource.CLOSED) {
        esRef.current = null;
      }
    };

    return () => {
      try { es.close(); } catch { /* noop */ }
      esRef.current = null;
    };
  }, [token, adminToken, topics.join(","), onEvent]);
}
